import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Card — the panel everything on a page sits inside.
 *
 * Composed of parts rather than driven by props (`title=`, `actions=`,
 * `footer=`). A props-driven card looks tidier for the first three uses and
 * then grows a prop per variation until nobody can tell what it renders. Parts
 * let a caller put a filter row between the header and the table without
 * anyone adding `betweenHeaderAndContent`.
 *
 * Depth comes from a hairline border, not a shadow — both references get their
 * layering from surface tint alone, and shadows in a dense financial UI make
 * the page look like a pile of receipts.
 */

export function Card({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'bg-raised border border-subtle rounded-(--card-radius) shadow-(--shadow-raised)',
        // Clips the top corners of a table that runs edge to edge inside.
        'overflow-hidden',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * Header. `bordered` draws a rule beneath it — right when the content below is
 * a table that runs to the edges, wrong when the content is padded prose,
 * because the rule then floats away from what it separates.
 */
export function CardHeader({
  className,
  bordered = false,
  actions,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { bordered?: boolean; actions?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4',
        'px-(--card-padding) pt-(--card-padding)',
        bordered && 'pb-(--card-padding) border-b border-subtle',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0 flex flex-col gap-(--card-header-gap)">{children}</div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 className={cn('text-lg font-semibold text-primary tracking-tight', className)} {...rest}>
      {children}
    </h2>
  );
}

/**
 * The supporting line under a title.
 *
 * Worth using generously. Both references lean on it, and in an accounting tool
 * it is where a screen earns trust — "debits and credits must agree exactly"
 * tells someone what they are looking at far better than a bare heading does.
 */
export function CardDescription({ className, children, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-md text-tertiary leading-snug max-w-[75ch]', className)} {...rest}>
      {children}
    </p>
  );
}

/** Padded body. Use `flush` when the content is a table that should meet the edges. */
export function CardContent({
  className,
  flush = false,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { flush?: boolean }) {
  return (
    <div className={cn(!flush && 'p-(--card-padding)', className)} {...rest}>
      {children}
    </div>
  );
}

